import React, { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import AppShell from "../components/AppShell";
import PageHero from "../components/PageHero";
import PlanWelcomeHero from "../components/PlanWelcomeHero";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";

// ── BillingSuccess ─────────────────────────────────────────────────────
// Landing page users hit after Stripe returns them from Checkout
// (`/billing/success?session_id=…`). We poll `/payments/status/{sid}`
// until Stripe reports `payment_status = paid`, then refresh the auth
// context so `user.package_slug` reflects the just-purchased tier and
// render the branded post-subscription hero (PlanWelcomeHero) which
// switches copy + membership badge dynamically per plan.
// ─────────────────────────────────────────────────────────────────────────
export function BillingSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ status: "polling", payment_status: "pending" });
  const { user, refresh } = useAuth();

  useEffect(() => {
    if (!sessionId) return;
    let attempts = 0;
    let cancelled = false;
    const tick = async () => {
      if (cancelled) return;
      attempts += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (cancelled) return;
        setState(data);
        if (data.payment_status === "paid") {
          // Refresh so useAuth().user.package_slug reflects the new tier
          // before PlanWelcomeHero reads it below.
          refresh();
          return;
        }
      } catch { /* transient — polling loop will retry */ }
      if (attempts < 10 && !cancelled) setTimeout(tick, 2000);
    };
    tick();
    return () => { cancelled = true; };
  }, [sessionId, refresh]);

  const paid = state.payment_status === "paid";
  const failed = ["failed", "expired"].includes(state.payment_status);
  // QA aid: when `?preview=1` is on the URL, render the paid hero
  // immediately without polling Stripe. Never surfaces to real users
  // (Stripe never sends them here with that flag) and lets the team
  // sanity-check the branded hero end-to-end in preview + production.
  const previewMode = params.get("preview") === "1";
  const showPaidHero = paid || previewMode;

  return (
    <AppShell>
      <div className="w-full">
        {showPaidHero ? (
          <PlanWelcomeHero user={user} />
        ) : (
          <>
            <PageHero
              eyebrow="Billing"
              icon={failed ? XCircle : Loader2}
              title={failed ? "Payment issue" : "Confirming your payment…"}
              subtitle={
                failed
                  ? "We couldn't confirm your payment. Please try again or contact support."
                  : "Hold on — polling Stripe. This usually resolves in a few seconds."
              }
            />
            <div
              className="mt-6 flex items-start gap-4 rounded-2xl border border-border bg-card p-6"
              data-testid="billing-status-card"
            >
              {failed ? (
                <XCircle className="mt-1 h-6 w-6 text-destructive" />
              ) : (
                <Loader2 className="mt-1 h-6 w-6 animate-spin" />
              )}
              <div className="flex-1">
                <p className="font-display text-2xl">
                  {failed ? "We couldn't confirm your payment" : "Hold on — polling Stripe…"}
                </p>
                <p className="mt-1 text-sm text-muted-foreground">
                  {failed ? "Please try again or contact support." : "This usually resolves in a few seconds."}
                </p>
                {failed && (
                  <div className="mt-6 flex gap-3">
                    <Link
                      to="/account/subscription"
                      data-testid="billing-back-pricing"
                      className="rounded-full border border-border px-5 py-2 text-sm transition-colors hover:bg-secondary"
                    >
                      Back to pricing
                    </Link>
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </AppShell>
  );
}
